import { useAtomValue } from "jotai";
import { useCallback } from "react";
import type { QuestionProps } from "../../../components/Questionnaire/Section/Question/types";
import { useCoverGeniusSeeMoreCoversState } from "../../../hooks/useCoverGeniusSeeMoreCoversState";
import { usePrepopulateAnswers } from "../../../hooks/usePrepopulateAnswers/usePrepopulateAnswers";
import { useQuestionRefs } from "../../../hooks/useQuestionRefs/useQuestionRefs";
import { useSendAnswers } from "../../../hooks/useSendAnswers";
import { useSetApiError } from "../../../hooks/useApiError";
import { useQuestionnaireActions } from "./useQuestionnaireActions";
import { useCurrentSection } from "./useCurrentSection";
import { useQuestions } from "./useQuestions";
import { useSections } from "./useSections";
import { useQuestionnaire } from "./useQuestionnaire";
import { useAnswers } from "./useAnswers";
import { useAnswer } from "./useAnswer";
import { questionnaireFormRefAtom } from "../state/questionnaireFormRefAtom";
import { useSnowplowContext } from "@simplybusiness/services";
import { useNavEventCategory } from "./useNavEventCategory";
import {
  shouldTriggerValidationEvent,
  getGroupName,
} from "../../../components/Questionnaire/Section/Question/QuestionFactory/shouldTriggerValidationEvent";
import { useGroups } from "./useGroups";

/**
 * Questionnaire navigation and validated progression.
 * Handles both simple navigation (goBack, goNext) and validated progression (validateAndProgress).
 *
 * @returns { goBack, goNext, handleBackClick, validateAndProgress, handleNextClick, handleSeeMoreCovers, showSeeMoreCoversTrigger }
 * @example const { goBack, handleBackClick, handleNextClick, validateAndProgress } = useQuestionnaireNavigation();
 */
export const useQuestionnaireNavigation = () => {
  const sections = useSections();
  const { navigatePrev, navigateNext, setCurrentSectionIndex } =
    useQuestionnaireActions();
  const { currentSection, currentSectionIndex, isLastSection } =
    useCurrentSection(sections);
  const questions = useQuestions();
  const [showSeeMoreCoversTrigger, seeMoreCovers] =
    useCoverGeniusSeeMoreCoversState();
  const state = useQuestionnaire();
  const { validateForm, setErrors } = state;
  const answers = useAnswers();
  const landlordType = useAnswer("Landlord type");
  const { sendAnswers } = useSendAnswers();
  const { prepopulateAnswers } = usePrepopulateAnswers();
  const { focusFirstError } = useQuestionRefs();
  const formRef = useAtomValue(questionnaireFormRefAtom);
  const setApiError = useSetApiError();
  const { snowplow } = useSnowplowContext();
  const category = useNavEventCategory();
  const groups = useGroups();

  /**
   * Navigate to the previous section
   */
  const goBack = useCallback(() => {
    navigatePrev();
  }, [navigatePrev]);

  /**
   * Navigate to the next section
   */
  const goNext = useCallback(() => {
    navigateNext();
  }, [navigateNext]);

  /**
   * Handle Cover Genius "see more covers" button click
   * Shows all suggested questions when clicked
   */
  const handleSeeMoreCovers = useCallback(() => {
    // Track analytics event
    snowplow?.trigger("seeMoreClicked", {});

    // Hide the "see more covers" trigger
    seeMoreCovers(false);

    // Find all suggested questions in the current section
    const questionNames =
      currentSection?.questions
        .filter(
          (q): q is QuestionProps => "suggested" in q && q.suggested === true,
        )
        .map(({ name }) => name) ?? [];

    // Show all suggested questions
    questions.show(questionNames);
  }, [seeMoreCovers, questions, currentSection, snowplow]);

  /**
   * Validate current section and proceed to next section or submit form
   * Handles:
   * - Landlord type special case (prepopulate answers)
   * - Earlier section validation errors (navigate to first invalid section)
   * - Current section validation errors (show errors)
   * - Form submission (send answers and redirect or navigate)
   */
  const validateAndProgress = useCallback(() => {
    let isValid: boolean;
    let errors: Record<string, string>;
    let firstInvalidSectionIndex: number | undefined;

    try {
      const validate = () => validateForm(state);
      ({ isValid, errors, firstInvalidSectionIndex } = validate());
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setApiError("Something went wrong. Please try again.");
      console.error(err);
      return;
    }

    // Landlord type special case: prepopulate commercial property answers
    if (!!landlordType && landlordType !== "residential") {
      prepopulateAnswers({
        ...answers,
        "Landlord type": landlordType,
        "Property type": landlordType,
      }).catch(() => {
        setErrors({ "Landlord type": "Unable to continue" });
      });

      return;
    }

    // Check if an earlier section has validation errors
    const earlierSectionInvalid =
      !isValid &&
      firstInvalidSectionIndex !== undefined &&
      firstInvalidSectionIndex < currentSectionIndex;

    // If there is an error in an earlier section, navigate to it
    if (earlierSectionInvalid && firstInvalidSectionIndex !== undefined) {
      setCurrentSectionIndex(firstInvalidSectionIndex);
      return;
    }

    // Only check validity for current section, ignore future section errors
    const currentSectionInvalid =
      !isValid &&
      firstInvalidSectionIndex !== undefined &&
      firstInvalidSectionIndex === currentSectionIndex;

    if (currentSectionInvalid) {
      setErrors(errors);

      // Trigger validationShown events for personalised cover questions
      if (currentSection && snowplow) {
        Object.keys(errors).forEach(questionName => {
          // Find the question in the current section
          const question = currentSection.questions.find(
            (q): q is QuestionProps => "name" in q && q.name === questionName,
          );

          if (question && "display" in question) {
            // Check if this is a personalised cover question (parent or child)
            if (
              shouldTriggerValidationEvent(
                question.display,
                questionName,
                groups,
              )
            ) {
              const coverName = getGroupName(questionName, groups);
              snowplow.trigger("validationShown", {
                cover: coverName,
                question: questionName,
              });
            }
          }
        });
      }

      focusFirstError(errors);
      return;
    }

    // All validation passed - send answers to API
    sendAnswers(answers)
      .then(redirectUrl => {
        // Use the redirect URL returned from sendAnswers (from the API response)
        // This avoids stale closure issues where the redirectUrl would be undefined
        if (redirectUrl) {
          window.location.assign(redirectUrl);
        } else if (isLastSection) {
          // On the last section with no redirect URL, submit the form natively.
          // This handles cases where the backend doesn't return a redirectUrl
          // but expects a form POST to proceed (e.g., inland marine Q2Q).
          // Use setTimeout to ensure React finishes any pending renders before submitting.
          setTimeout(() => {
            formRef?.current?.submit();
          }, 0);
        } else {
          goNext();
        }
      })
      .catch(error => {
        const err = error instanceof Error ? error : new Error(String(error));
        setApiError("Something went wrong. Please try again.");
        console.error(err);
      });
  }, [
    answers,
    currentSection,
    currentSectionIndex,
    groups,
    isLastSection,
    formRef,
    validateForm,
    state,
    setCurrentSectionIndex,
    setErrors,
    focusFirstError,
    sendAnswers,
    goNext,
    prepopulateAnswers,
    landlordType,
    setApiError,
    snowplow,
  ]);

  /**
   * Handle Next button click with analytics tracking
   * Triggers Snowplow event and then validates/navigates via validateAndProgress
   */
  const handleNextClick = useCallback(() => {
    snowplow?.trigger("navButtonClicked", {
      label: "Next button",
      category,
    });
    validateAndProgress();
  }, [validateAndProgress, snowplow, category]);

  /**
   * Handle Back button click with analytics tracking
   * Triggers Snowplow event and then navigates to previous section
   */
  const handleBackClick = useCallback(() => {
    snowplow?.trigger("navButtonClicked", {
      label: "Back button",
      category,
    });
    goBack();
  }, [goBack, snowplow, category]);

  return {
    goBack,
    goNext,
    handleBackClick,
    validateAndProgress,
    handleNextClick,
    handleSeeMoreCovers,
    showSeeMoreCoversTrigger,
  };
};
